import { useEffect, useRef } from 'react';
import { Select } from 'antd';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import CustomIcon from '@components/shared/CustomIcon';
import { flow } from 'lodash';
import { historyType, marketType } from '@propTypes';
import globalActions from '@store/actions';
import actions from '@store/actions/navigation';
import { useTranslation } from 'react-i18next';
import { generateBuildingsOptionGroup, generateLocationsOptionGroup } from './OptionGroups';
import s from './UniversalSearch.module.less';
import useUniversalSearch from './useUniversalSearch';

const MagnifyingGlassIcon = () => <CustomIcon type="magnifying-glass" className={s.searchIcon} />;
const ClearIcon = props => (
  <div className={s.clearIconContainer}>
    <CustomIcon {...props} type="close" className={s.clearIcon} />
  </div>
);

/* DEBT!
 This universal search where we are using ANTD instead of a custom compomnent,
 is known technical and product debt.

 When its changed please refer to the comments of PRODUCT-17281 for some context learned about
 things to keep in mind while changing this.
*/

export const RawUniversalSearch = ({
  history,
  marketsBySlug,
  startingMarketSlugs,
  visible,
  handleSelect,
  marketSlug,
  queryString,
  searchBuildingClicked,
  searchLocationAdded,
}) => {
  const {
    resetSearch,
    searchValues,
    onSelect,
    onDeselect,
    onSearch,
    inputValue,
    buildings,
    locations,
    isUsingMap,
  } = useUniversalSearch({
    history,
    queryString,
    startingMarketSlugs,
    marketsBySlug,
    marketSlug,
  });

  const dispatch = useDispatch();
  const { t } = useTranslation('navigation');
  const inputRef = useRef();

  const handleSelectTrackers = value => {
    if (value.key.includes('building')) {
      searchBuildingClicked();
    }
  };

  const handleLocationFilterChange = values => {
    const currentValuesOnState = searchValues.map(val => val.key);
    const upcomingValuesInSearchBar = values.map(val => val.key);

    const upcomingLocationFilters = upcomingValuesInSearchBar.filter(
      upcomingValue => !upcomingValue.includes('building'),
    );

    const hasNewLocationFilters = !!upcomingLocationFilters.filter(
      upcomingValue => !currentValuesOnState.includes(upcomingValue),
    ).length;

    dispatch(globalActions.setDrawnPolygons([]));
    if (hasNewLocationFilters) {
      searchLocationAdded(upcomingLocationFilters.join(','));
    }
  };

  useEffect(() => {
    if (visible) {
      const input = inputRef.current.querySelector('input');
      input.focus();
    }
  }, [visible]);

  const hasSearchResults = () => buildings.length || locations.length;

  const renderDropdown = menu => (
    <div className={s.universalSearchDropdown}>{hasSearchResults() ? menu : null}</div>
  );

  return (
    <div id="universal-search" data-testid="UniversalSearch" ref={inputRef}>
      <Select
        mode="multiple"
        className={s.universalSearch}
        suffixIcon={<MagnifyingGlassIcon className={s.magnifyingGlassIcon} />}
        showSearch
        allowClear
        clearIcon={<ClearIcon onClick={resetSearch} />}
        defaultActiveFirstOption={false}
        size="large"
        showArrow
        placeholder={t('searchPlaceholder')}
        filterOption={false}
        onSearch={onSearch}
        onSelect={value => {
          handleSelectTrackers(value);
          onSelect(value);
          handleSelect();
        }}
        onChange={handleLocationFilterChange}
        onDeselect={onDeselect}
        notFoundContent={null}
        data-testid="search"
        optionLabelProp="label"
        dropdownRender={renderDropdown}
        menuItemSelectedIcon={<CustomIcon type="checkmark" className={s.selectedCheckmark} />}
        value={searchValues}
        labelInValue
        dropdownAlign={{
          points: ['tl', 'bl'],
          offset: [0, 0],
        }}
      >
        {isUsingMap && (
          <Select.Option
            style={{ display: 'none' }}
            key="custom-map"
            value="custom-map"
            data-testid="custom-map-option"
            label={t('customMapAreaLabel')}
          />
        )}
        {!!locations.length && generateLocationsOptionGroup(locations, inputValue)}
        {!!buildings.length && generateBuildingsOptionGroup(buildings, inputValue)}
      </Select>
    </div>
  );
};

RawUniversalSearch.defaultProps = {
  handleSelect: () => {},
  visible: true,
  queryString: '',
  marketSlug: null,
};

RawUniversalSearch.propTypes = {
  history: historyType.isRequired,
  marketsBySlug: PropTypes.shape({
    [PropTypes.string]: marketType,
  }).isRequired,
  startingMarketSlugs: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleSelect: PropTypes.func,
  visible: PropTypes.bool,
  queryString: PropTypes.string,
  marketSlug: PropTypes.string,
  searchBuildingClicked: PropTypes.func.isRequired,
  searchLocationAdded: PropTypes.func.isRequired,
};

const mapStateToProps = (state, props) => ({
  marketsBySlug: state.shapes.bySlug,
  startingMarketSlugs: state.shapes.startingSlugs,
  marketSlug: props.match?.params?.marketSlug,
  queryString: props.location.search,
});

const mapDispatchToProps = dispatch => ({
  searchBuildingClicked: () => dispatch(actions.searchBuildingClicked()),
  searchLocationAdded: slugs => dispatch(actions.searchLocationAdded(slugs)),
});

export default flow(withRouter, connect(mapStateToProps, mapDispatchToProps))(RawUniversalSearch);
